import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Activity,
  Calendar,
  Clock,
  Mail,
  Phone,
  Building2,
  BarChart,
  Gift,
  Sparkles,
  FileText,
  MessageSquare,
  Receipt,
  History,
  CheckCircle,
  XCircle,
  Package,
  Download,
  Eye
} from 'lucide-react';
import api from '../services/api';

interface UserDetail {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  is_active: boolean;
  phone: string;
  company: string;
  date_joined: string;
  last_login: string;
  last_activity: string;
  is_online: boolean;
  projects_count: number;
  invoices_count: number;
}

interface PromoCode {
  id: number;
  code: string;
  discount_percentage: number;
  is_used: boolean;
  used_at: string | null;
  created_at: string;
  invoice_number?: string;
}

interface WheelSpin {
  id: number;
  prize: string;
  discount_percentage: number;
  spun_at: string;
}

interface Project {
  id: number;
  title: string;
  description: string;
  status: string;
  created_at: string;
}

interface Message {
  id: number;
  subject: string;
  content: string;
  created_at: string;
  is_read: boolean;
}

interface InvoiceHistoryEntry {
  id: number;
  action: string;
  action_code: string;
  previous_status: string | null;
  new_status: string;
  new_status_display: string;
  changed_by: string;
  comment: string;
  created_at: string;
}

interface Invoice {
  id: number;
  invoice_id: string;
  invoice_number: string;
  amount: number;
  subtotal: number;
  discount_amount: number;
  tva_amount: number;
  status: string;
  status_display: string;
  promo_code: string | null;
  client_name: string;
  client_email: string;
  client_company: string;
  created_at: string;
  updated_at: string;
  history: InvoiceHistoryEntry[];
  items_count: number;
}

interface HistoryItem {
  id: number;
  type: string; // 'promo', 'invoice', 'message', 'project', 'wheel'
  description: string;
  timestamp: string;
  metadata?: any;
}

interface UserDetailModalProps {
  user: UserDetail;
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'infos' | 'promos' | 'wheel' | 'projects' | 'messages' | 'invoices' | 'history';

const UserDetailModal: React.FC<UserDetailModalProps> = ({ user, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('infos');
  const [loading, setLoading] = useState(false);
  
  // États pour les données
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [wheelSpins, setWheelSpins] = useState<WheelSpin[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);

  useEffect(() => {
    if (activeTab !== 'infos') {
      loadTabData(activeTab);
    }
  }, [activeTab, user.id]);

  const loadTabData = async (tab: TabType) => {
    setLoading(true);

    try {
      switch (tab) {
        case 'promos':
          const promosRes = await api.get(`/api/users/${user.id}/promos/`);
          setPromos(promosRes.data);
          break;

        case 'wheel':
          const wheelRes = await api.get(`/api/users/${user.id}/wheel-spins/`);
          setWheelSpins(wheelRes.data);
          break;

        case 'projects':
          const projectsRes = await api.get(`/api/users/${user.id}/projects/`);
          setProjects(projectsRes.data);
          break;

        case 'messages':
          const messagesRes = await api.get(`/api/users/${user.id}/messages/`);
          setMessages(messagesRes.data);
          break;

        case 'invoices':
          const invoicesRes = await api.get(`/api/users/${user.id}/invoices/`);
          setInvoices(invoicesRes.data);
          break;

        case 'history':
          const historyRes = await api.get(`/api/users/${user.id}/history/`);
          setHistory(historyRes.data);
          break;
      }
    } catch (error) {
      console.error(`Erreur lors du chargement de ${tab}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Non disponible';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'infos', label: 'Infos', icon: User },
    { id: 'promos', label: 'Promos', icon: Gift },
    { id: 'wheel', label: 'Roue', icon: Sparkles },
    { id: 'projects', label: 'Projets', icon: Package },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'invoices', label: 'Factures', icon: Receipt },
    { id: 'history', label: 'Historique', icon: History }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white relative flex-shrink-0">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
                {user.first_name.charAt(0)}{user.last_name.charAt(0)}
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {user.first_name} {user.last_name}
                </h2>
                <p className="text-white/80 flex items-center space-x-2 mt-1">
                  <Mail className="w-4 h-4" />
                  <span>{user.email}</span>
                </p>
                <div className="flex items-center space-x-2 mt-2">
                  {user.is_online ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-400 text-white">
                      <Activity className="w-3 h-3 mr-1" />
                      En ligne
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-400 text-white">
                      Hors ligne
                    </span>
                  )}
                  {user.is_admin && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-400 text-white">
                      👑 Admin
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 bg-white flex-shrink-0">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabType)}
                    className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium text-sm">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <>
                {/* Onglet Infos */}
                {activeTab === 'infos' && (
                  <InfosTab user={user} formatDate={formatDate} />
                )}

                {/* Onglet Promos */}
                {activeTab === 'promos' && (
                  <PromosTab promos={promos} formatDate={formatDate} />
                )}

                {/* Onglet Roue */}
                {activeTab === 'wheel' && (
                  <WheelTab spins={wheelSpins} formatDate={formatDate} />
                )}

                {/* Onglet Projets */}
                {activeTab === 'projects' && (
                  <ProjectsTab projects={projects} formatDate={formatDate} />
                )}

                {/* Onglet Messages */}
                {activeTab === 'messages' && (
                  <MessagesTab messages={messages} formatDate={formatDate} />
                )}

                {/* Onglet Factures */}
                {activeTab === 'invoices' && (
                  <InvoicesTab invoices={invoices} formatDate={formatDate} />
                )}

                {/* Onglet Historique */}
                {activeTab === 'history' && (
                  <HistoryTab history={history} formatDate={formatDate} />
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Fermer
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============ COMPOSANTS ONGLETS ============

const InfosTab: React.FC<{ user: UserDetail; formatDate: (date: string) => string }> = ({ user, formatDate }) => (
  <div className="space-y-6">
    {/* Statut */}
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
        <Activity className="w-5 h-5 mr-2 text-indigo-600" />
        Statut
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Connexion</p>
          {user.is_online ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <Activity className="w-4 h-4 mr-1" />
              En ligne
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              Hors ligne
            </span>
          )}
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Compte</p>
          {user.is_active ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              Actif
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
              Inactif
            </span>
          )}
        </div>
      </div>
    </div>

    {/* Informations de contact */}
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
        <User className="w-5 h-5 mr-2 text-indigo-600" />
        Informations de contact
      </h3>
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Téléphone :</span>
          <span className="text-sm font-medium text-gray-900">{user.phone || 'Non renseigné'}</span>
        </div>
        {user.company && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Entreprise :</span>
            <span className="text-sm font-medium text-gray-900">{user.company}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Rôle :</span>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            user.is_admin 
              ? 'bg-purple-100 text-purple-800' 
              : 'bg-blue-100 text-blue-800'
          }`}>
            {user.is_admin ? '👑 Administrateur' : '👤 Client'}
          </span>
        </div>
      </div>
    </div>

    {/* Statistiques */}
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
        <BarChart className="w-5 h-5 mr-2 text-indigo-600" />
        Statistiques
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
          <p className="text-sm text-gray-600 mb-1">Projets</p>
          <p className="text-3xl font-bold text-indigo-600">{user.projects_count || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
          <p className="text-sm text-gray-600 mb-1">Factures</p>
          <p className="text-3xl font-bold text-purple-600">{user.invoices_count || 0}</p>
        </div>
      </div>
    </div>

    {/* Dates */}
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
        <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
        Dates importantes
      </h3>
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            Inscription :
          </span>
          <span className="text-sm font-medium text-gray-900">
            {formatDate(user.date_joined)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            Dernière activité :
          </span>
          <span className="text-sm font-medium text-gray-900">
            {formatDate(user.last_activity || user.last_login)}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const PromosTab: React.FC<{ promos: PromoCode[]; formatDate: (date: string) => string }> = ({ promos, formatDate }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
      <Gift className="w-5 h-5 mr-2 text-indigo-600" />
      Codes promo ({promos.length})
    </h3>
    {promos.length === 0 ? (
      <div className="text-center py-12 text-gray-500">
        <Gift className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p>Aucun code promo</p>
      </div>
    ) : (
      <div className="space-y-3">
        {promos.map((promo) => (
          <div key={promo.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-br from-pink-500 to-purple-600 text-white px-3 py-1 rounded-lg font-mono font-bold">
                  {promo.code}
                </div>
                <span className="text-lg font-bold text-indigo-600">-{promo.discount_percentage}%</span>
              </div>
              {promo.is_used ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Utilisé
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Disponible
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              <div>Créé le : {formatDate(promo.created_at)}</div>
              {promo.is_used && (
                <>
                  <div>Utilisé le : {formatDate(promo.used_at!)}</div>
                  {promo.invoice_number && (
                    <div className="flex items-center space-x-1">
                      <Receipt className="w-3 h-3" />
                      <span>Facture : <span className="font-semibold">{promo.invoice_number}</span></span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const WheelTab: React.FC<{ spins: WheelSpin[]; formatDate: (date: string) => string }> = ({ spins, formatDate }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
      <Sparkles className="w-5 h-5 mr-2 text-indigo-600" />
      Historique de la roue ({spins.length})
    </h3>
    {spins.length === 0 ? (
      <div className="text-center py-12 text-gray-500">
        <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p>Aucun tour de roue</p>
      </div>
    ) : (
      <div className="space-y-3">
        {spins.map((spin) => (
          <div key={spin.id} className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <span className="font-semibold text-gray-900">{spin.prize}</span>
              </div>
              <span className="text-lg font-bold text-amber-600">-{spin.discount_percentage}%</span>
            </div>
            <div className="text-xs text-gray-600">
              {formatDate(spin.spun_at)}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ProjectsTab: React.FC<{ projects: Project[]; formatDate: (date: string) => string }> = ({ projects, formatDate }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
      <Package className="w-5 h-5 mr-2 text-indigo-600" />
      Projets ({projects.length})
    </h3>
    {projects.length === 0 ? (
      <div className="text-center py-12 text-gray-500">
        <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p>Aucun projet</p>
      </div>
    ) : (
      <div className="space-y-3">
        {projects.map((project) => (
          <div key={project.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-semibold text-gray-900">{project.title}</h4>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                project.status === 'completed' ? 'bg-green-100 text-green-800' :
                project.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {project.status}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-2">{project.description}</p>
            <div className="text-xs text-gray-500">{formatDate(project.created_at)}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const MessagesTab: React.FC<{ messages: Message[]; formatDate: (date: string) => string }> = ({ messages, formatDate }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
      <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
      Messages ({messages.length})
    </h3>
    {messages.length === 0 ? (
      <div className="text-center py-12 text-gray-500">
        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p>Aucun message</p>
      </div>
    ) : (
      <div className="space-y-3">
        {messages.map((message) => (
          <div key={message.id} className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${
            !message.is_read ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200'
          }`}>
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-semibold text-gray-900">{message.subject}</h4>
              {!message.is_read && (
                <span className="bg-indigo-500 text-white px-2 py-1 rounded text-xs">Nouveau</span>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-2">{message.content}</p>
            <div className="text-xs text-gray-500">{formatDate(message.created_at)}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const InvoicesTab: React.FC<{ invoices: Invoice[]; formatDate: (date: string) => string }> = ({ invoices, formatDate }) => {
  const navigate = useNavigate();
  const [expandedInvoice, setExpandedInvoice] = React.useState<number | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'revision':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewInvoice = (invoiceId: number) => {
    // Naviguer vers la page facture proforma en mode aperçu uniquement
    navigate('/facture-proforma', {
      state: {
        purchaseRequestId: invoiceId,
        viewMode: true  // Mode aperçu uniquement
      }
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
        <Receipt className="w-5 h-5 mr-2 text-indigo-600" />
        Factures ({invoices.length})
      </h3>
      {invoices.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>Aucune facture</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* En-tête de la facture */}
              <div className="p-4">
                <div 
                  className="cursor-pointer"
                  onClick={() => setExpandedInvoice(expandedInvoice === invoice.id ? null : invoice.id)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Receipt className="w-5 h-5 text-indigo-600" />
                      <div>
                        <span className="font-semibold text-gray-900">{invoice.invoice_number}</span>
                        {invoice.invoice_id && (
                          <span className="text-xs text-gray-500 ml-2">({invoice.invoice_id})</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewInvoice(invoice.id);
                        }}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs font-medium"
                        title="Voir la facture en mode aperçu"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Ouvrir</span>
                      </button>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                        {invoice.status_display}
                      </span>
                    </div>
                  </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sous-total :</span>
                    <span className="font-medium">{invoice.subtotal.toLocaleString('fr-FR')} FCFA</span>
                  </div>
                  {invoice.discount_amount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Réduction :</span>
                      <span className="font-medium">-{invoice.discount_amount.toLocaleString('fr-FR')} FCFA</span>
                    </div>
                  )}
                  {invoice.tva_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">TVA :</span>
                      <span className="font-medium">{invoice.tva_amount.toLocaleString('fr-FR')} FCFA</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-semibold">Total :</span>
                    <span className="font-bold text-indigo-600">{invoice.amount.toLocaleString('fr-FR')} FCFA</span>
                  </div>
                </div>
                
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{formatDate(invoice.created_at)}</span>
                    <span className="flex items-center">
                      {expandedInvoice === invoice.id ? '▼' : '▶'} Voir l'historique ({invoice.history.length})
                    </span>
                  </div>
                </div>
              </div>

              {/* Historique du processus */}
              {expandedInvoice === invoice.id && invoice.history.length > 0 && (
                <div className="border-t border-gray-200 bg-gray-50 p-4">
                  <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center">
                    <History className="w-4 h-4 mr-2" />
                    Historique du processus
                  </h4>
                  <div className="space-y-2">
                    {invoice.history.map((entry, index) => (
                      <div key={entry.id} className="flex items-start space-x-3 text-sm">
                        <div className="flex-shrink-0 mt-1">
                          <div className={`w-2 h-2 rounded-full ${
                            index === invoice.history.length - 1 ? 'bg-indigo-600' : 'bg-gray-400'
                          }`} />
                        </div>
                        <div className="flex-1 pb-3 border-l-2 border-gray-200 pl-3 ml-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-900">{entry.action}</span>
                            <span className="text-xs text-gray-500">{formatDate(entry.created_at)}</span>
                          </div>
                          {entry.previous_status && (
                            <div className="text-xs text-gray-600 mb-1">
                              {entry.previous_status} → <span className="font-semibold">{entry.new_status_display}</span>
                            </div>
                          )}
                          {entry.comment && (
                            <p className="text-xs text-gray-600 italic">{entry.comment}</p>
                          )}
                          <p className="text-xs text-gray-500">Par : {entry.changed_by}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HistoryTab: React.FC<{ history: HistoryItem[]; formatDate: (date: string) => string }> = ({ history, formatDate }) => {
  const navigate = useNavigate();
  
  const getIcon = (type: string) => {
    switch (type) {
      case 'promo': return <Gift className="w-4 h-4" />;
      case 'invoice': return <Receipt className="w-4 h-4" />;
      case 'message': return <MessageSquare className="w-4 h-4" />;
      case 'project': return <Package className="w-4 h-4" />;
      case 'wheel': return <Sparkles className="w-4 h-4" />;
      default: return <History className="w-4 h-4" />;
    }
  };

  const getColor = (type: string) => {
    switch (type) {
      case 'promo': return 'bg-pink-100 text-pink-800';
      case 'invoice': return 'bg-green-100 text-green-800';
      case 'message': return 'bg-blue-100 text-blue-800';
      case 'project': return 'bg-purple-100 text-purple-800';
      case 'wheel': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'revision':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewInvoice = (invoiceId: number) => {
    navigate('/facture-proforma', {
      state: {
        purchaseRequestId: invoiceId,
        viewMode: true
      }
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
        <History className="w-5 h-5 mr-2 text-indigo-600" />
        Historique complet ({history.length})
      </h3>
      {history.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <History className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>Aucun historique</p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className={`p-2 rounded-lg ${getColor(item.type)}`}>
                    {getIcon(item.type)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{item.description}</p>
                    
                    {/* Informations supplémentaires pour les factures */}
                    {item.type === 'invoice' && item.metadata && (
                      <div className="mt-2 flex items-center space-x-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          getStatusColor(item.metadata.status)
                        }`}>
                          {item.metadata.status_display}
                        </span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs font-semibold text-indigo-600">
                          {item.metadata.amount?.toLocaleString('fr-FR')} FCFA
                        </span>
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-1">{formatDate(item.timestamp)}</p>
                  </div>
                </div>
                
                {/* Bouton pour ouvrir la facture */}
                {item.type === 'invoice' && item.metadata?.invoice_id && (
                  <button
                    onClick={() => handleViewInvoice(item.metadata.invoice_id)}
                    className="ml-3 flex items-center space-x-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors text-xs font-medium"
                    title="Voir la facture"
                  >
                    <Eye className="w-3 h-3" />
                    <span>Voir</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserDetailModal;
